import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { SeoRunStatus } from '../../../../shared/seo-run';
import { api, withClientTimeout } from '../../lib/api';

const keyFor = (brandId:string) => `hc:seo-request:${brandId}`;
/** Persist before POST. Failed/uncertain responses must never discard this identity. */
export function seoRequestId(brandId:string, storage:Pick<Storage,'getItem'|'setItem'> = localStorage) {
 const key = keyFor(brandId);
 const existing = storage.getItem(key);
 if (existing && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(existing)) return existing;
 const id = crypto.randomUUID();
 storage.setItem(key,id);
 return id;
}
export function SeoResearchControl({brandId}:{brandId:string}) {
 // A brand change must discard state before issuing reads for the new brand.
 return <BrandSeoResearchControl key={brandId} brandId={brandId}/>;
}
function BrandSeoResearchControl({brandId}:{brandId:string}) {
 const [requestId,setRequestId] = useState<string|null>(null);
 const [status,setStatus] = useState<SeoRunStatus|null>(null);
 const [launchError,setLaunchError] = useState<string|null>(null);
 const [statusError,setStatusError] = useState<string|null>(null);
 const [storageError,setStorageError] = useState<string|null>(null);
 const [busy,setBusy] = useState(false);
 const active = useRef(false);
 const base = `/api/brands/${encodeURIComponent(brandId)}/seo-research`;
 useEffect(()=>{
  active.current = true;
  try {setRequestId(localStorage.getItem(keyFor(brandId)));}
  catch {setStorageError('Browser storage is unavailable. Enable storage before launching SEO research.');setRequestId(null);}
  return ()=>{active.current = false;};
 },[brandId]);
 useEffect(()=>{
  if (!requestId || status?.status === 'completed' || status?.status === 'review_required' || status?.status === 'failed') return;
  let active = true;
  let reading = false;
   async function read() {
   if (reading) return;
   reading = true;
   try {
    const response = await withClientTimeout(signal=>api.get<{data:SeoRunStatus}>(`${base}/${requestId}`,signal));
    if (active) {setStatus(response.data);setStatusError(null);}
   } catch (error) {if (active) setStatusError(error instanceof Error ? error.message:'Status unavailable');}
   finally {reading = false;}
  }
  void read();
  const timer = setInterval(()=>void read(),5_000);
  return ()=>{active=false;clearInterval(timer);};
 },[base,requestId,status?.status]);
 async function launch(newRun = false) {
  if (busy) return;
  setBusy(true);
  let id:string;
  try {
   if (newRun && terminal) localStorage.removeItem(keyFor(brandId));
   id = seoRequestId(brandId);
   setStorageError(null);
   setRequestId(id);
   if (newRun) setStatus(null);
  } catch {
   setStorageError('Browser storage is unavailable. Enable storage before launching SEO research.');
   setBusy(false);return;
  }
  try {
   await withClientTimeout(signal=>api.post(base,{requestId:id},undefined,signal));
   if (!active.current) return;
   // Successful admission resolves the POST failure; a status read cannot do so.
   setLaunchError(null);
  } catch (error) {
   if (active.current) {setLaunchError(error instanceof Error ? error.message:'Launch unavailable; retain this request ID.');setBusy(false);}
   return;
  }
  try {
   const response = await withClientTimeout(signal=>api.get<{data:SeoRunStatus}>(`${base}/${id}`,signal));
   if (active.current) {setStatus(response.data);setStatusError(null);}
  } catch (error) {if (active.current) setStatusError(error instanceof Error ? error.message:'Status unavailable');}
  finally {if (active.current) setBusy(false);}
 }
 const terminal = status?.status === 'completed' || status?.status === 'review_required' || status?.status === 'failed';
 return <section className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" aria-label="SEO research">
  <h2 className="font-semibold text-slate-900">SEO research</h2>
  <p className="text-sm text-slate-600">Research keyword fit, measured search demand and analytics coverage. Results are saved so you can review and refine your keyword strategy.</p>
  <div className="flex gap-3 items-center">
   <button type="button" disabled={busy || terminal || Boolean(storageError && !requestId)} onClick={()=>void launch()}
    className="rounded-lg bg-brand-600 px-3 py-2 text-sm text-white disabled:opacity-50">
    {busy ? 'Checking Run…':requestId ? 'Retry / resume same Run':'Start SEO research'}
   </button>
   {terminal && <button type="button" disabled={busy} onClick={()=>void launch(true)} className="rounded-lg border px-3 py-2 text-sm">Start another SEO Run</button>}
   {status && <span role="status">{status.status === 'held' ? status.reason === 'invalid_evidence' ? 'Operator review required':'Waiting for confirmation':status.status === 'not_started' ? 'Not started':status.status === 'review_required' ? 'Review required':status.status}</span>}
  </div>
  {requestId && <p className="text-xs text-slate-500 break-all">Request: {requestId}</p>}
  {status?.status === 'held' && <p className="text-sm">{status.reason === 'invalid_evidence' ? 'Research evidence could not be validated. An authorized operator must review this Run. This request remains held.' : status.reason === 'evidence_unavailable' ? 'Research evidence is temporarily unavailable. Polling will check the same request; this Run remains held.' : 'Research is waiting for confirmation. Retry or resume to check the same research request.'}</p>}
  {status && <p className="text-sm">Cost: {status.costUsd === null ? 'unknown':`$${status.costUsd.toFixed(4)}`}</p>}
  {storageError && <p role="alert" className="text-sm text-red-700">{storageError}</p>}
  {launchError && <p role="alert" className="text-sm text-red-700">{launchError}</p>}
  {statusError && <p role="alert" className="text-sm text-red-700">{statusError}</p>}
  {status?.status === 'review_required' && <p className="text-sm">Some keyword assessments need more evidence before you use them. Review the saved research report.
   {status.review && <span> {status.review.acceptedCount} of {status.review.termCount} keyword assessments accepted; {status.review.researchMoreCount} needs more research.</span>}
  </p>}
  {(status?.status === 'completed' || status?.status === 'review_required') && status.result && <div className="space-y-3">
   <Link to={`/runs/${status.runId}`} className="text-brand-700 underline">Open saved Run</Link>
   <a href="#seo-research-result" className="ml-3 text-brand-700 underline">View research result</a>
   <p className="text-xs break-all">Saved result: {status.artifactId}</p>
   <div id="seo-research-result" className="overflow-x-auto"><table className="w-full text-sm text-left"><thead><tr><th>Keyword / fit</th><th>Search demand</th><th>Analytics</th></tr></thead><tbody>
    {status.result.candidates.map(c=><tr key={c.term} className="border-t align-top">
     <td className="py-2 pr-3">{c.term}<br/>{c.classification}<p>{c.reason}</p></td>
     <td className="py-2 pr-3">{c.demand.status}{c.demand.volumeBySource.map(v=><p key={v.source}>{v.source}: {v.volume ?? 'unknown'}</p>)}</td>
     <td className="py-2">{c.coverage.status}<p>Impressions: {c.coverage.impressions ?? 'unknown'} · Clicks: {c.coverage.clicks ?? 'unknown'}</p><p>Position: {c.coverage.averagePosition ?? 'unknown'}</p><p>{c.coverage.requestedStart} – {c.coverage.requestedEnd}</p></td>
    </tr>)}
   </tbody></table></div>
  </div>}
 </section>;
}
